import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useFetchMenu from '../../Hooks/UseFetchMenu';
import { API_BASE_URL } from '../../config';
import {
  FAMILY_FILTERS,
  getFamily,
  getStrength,
  getSweetness,
} from '../../utils/cocktailFlavorMap';
import styles from './CocktailFlavorMap.module.css';
import { formatPrice } from '../../utils/formatPrice';
import SmallNavBar from '../SmallNavBar/SmallNavBar';

const FAMILY_BADGE_CLASS = {
  refrescante: styles.badgeRefrescante,
  fuerte: styles.badgeFuerte,
  dulce: styles.badgeDulce,
  clasico: styles.badgeClasico,
  amargo: styles.badgeAmargo,
};

const FAMILY_LABEL = {
  refrescante: 'REFRESCANTE',
  fuerte: 'FUERTE',
  dulce: 'DULCE',
  clasico: 'CLÁSICO',
  amargo: 'AMARGO',
};

const CocktailFlavorMap = () => {
  const { id } = useParams();
  const { menuItems, loading, error } = useFetchMenu(API_BASE_URL);
  const [familyFilter, setFamilyFilter] = useState('all');

  const category = menuItems.find((cat) => String(cat.id) === String(id));

  const cocktails = useMemo(() => {
    const products = category?.products || [];
    return products.map((item) => ({
      ...item,
      family: getFamily(item),
      sweetness: getSweetness(item),
      strength: getStrength(item),
    }));
  }, [category]);

  const visibleCocktails = useMemo(() => {
    if (familyFilter === 'all') return cocktails;
    return cocktails.filter((c) => c.family === familyFilter);
  }, [cocktails, familyFilter]);

  if (loading) {
    return <div className={styles.status}>Cargando mapa...</div>;
  }

  if (error) {
    return <div className={styles.status}>Error: {error}</div>;
  }

  if (!category) {
    return <div className={styles.status}>Categoría no encontrada</div>;
  }

  return (
    <div className={styles.page}>
      {/* Misma barra de categorias que el resto del menu: sin ella, el mapa
          era un callejon sin salida para seguir navegando. */}
      <SmallNavBar />
      <header className={styles.header}>
        <Link to={`/Category/${category.id}`} className={styles.backButton}>
          <svg fill="none" height="20" stroke="currentColor" viewBox="0 0 24 24" width="20">
            <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
          </svg>
          Volver
        </Link>
        <div className={styles.badge}>RUSSAFA · CARTA VIVA</div>
      </header>

      <section className={styles.hero}>
        <h1 className={styles.heroTitle}>MAPA DE</h1>
        <h2 className={`${styles.heroTitle} ${styles.heroAccent}`}>SABORES</h2>
        <div className={styles.heroBar} />
      </section>

      <section className={styles.filters}>
        {FAMILY_FILTERS.map((filter) => (
          <button
            key={filter.id}
            type="button"
            className={`${styles.filterButton} ${familyFilter === filter.id ? styles.filterButtonActive : ''}`}
            onClick={() => setFamilyFilter(filter.id)}
          >
            {filter.label}
          </button>
        ))}
      </section>

      <main className={styles.gridSection}>
        {visibleCocktails.length === 0 ? (
          <p className={styles.empty}>No hay cócteles en este filtro.</p>
        ) : (
          <div className={styles.grid}>
            {visibleCocktails.map((cocktail) => (
              <div key={cocktail.id} className={styles.card}>
                <div className={styles.cardImageWrap}>
                  {cocktail.image?.small ? (
                    <img src={cocktail.image.small} alt={cocktail.name} className={styles.cardImage} />
                  ) : (
                    <div className={styles.cardImagePlaceholder}>🍸</div>
                  )}
                  <span className={`${styles.familyBadge} ${FAMILY_BADGE_CLASS[cocktail.family] || styles.badgeClasico}`}>
                    {FAMILY_LABEL[cocktail.family] || cocktail.family.toUpperCase()}
                  </span>
                  <span className={styles.priceTag}>{formatPrice(cocktail.price)}</span>
                </div>

                <div className={styles.cardBody}>
                  <h4 className={styles.cardName}>{cocktail.name}</h4>

                  <div className={styles.bars}>
                    <div className={styles.meter}>
                      <div className={styles.meterHeader}>
                        <span className={styles.barLabel}>Dulzor</span>
                      </div>
                      <div className={styles.barTrack}>
                        <div className={`${styles.barFill} ${styles.barSweetness}`} style={{ width: `${cocktail.sweetness}%` }} />
                      </div>
                    </div>
                    <div className={styles.meter}>
                      <div className={styles.meterHeader}>
                        <span className={styles.barLabel}>Fuerza</span>
                      </div>
                      <div className={styles.barTrack}>
                        <div className={`${styles.barFill} ${styles.barStrength}`} style={{ width: `${cocktail.strength}%` }} />
                      </div>
                    </div>
                  </div>

                  {cocktail.description && (
                    <p className={styles.cardDescription}>{cocktail.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CocktailFlavorMap;
